import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import { 
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, 
  Timestamp, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, 
  getDoc, runTransaction
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useAccount } from './AccountContext';

// Define types
export type TaxType = 'Income Tax' | 'VAT' | 'Corporate Tax' | 'Payroll Tax' | 'Property Tax' | 'Custom';

export type TaxFrequencyType = 'Annual' | 'Quarterly' | 'Monthly' | 'Custom';

export type TaxEntry = {
  id?: string;
  taxType: TaxType;
  name: string;
  description?: string;
  amount: number;
  rate?: number;
  frequency: TaxFrequencyType;
  dueDate: Date;
  paid: boolean;
  datePaid?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  budgetId?: string;
  accountId?: string; // Associated account ID
  previousAmount?: number; // Track previous amount for updates
  reminderScheduled?: boolean;
};

type TaxState = {
  taxEntries: TaxEntry[];
  loading: boolean;
  error: string | null;
};

type TaxAction =
  | { type: 'LOAD_TAX_ENTRIES'; payload: TaxEntry[] }
  | { type: 'ADD_TAX_ENTRY'; payload: TaxEntry }
  | { type: 'UPDATE_TAX_ENTRY'; payload: TaxEntry }
  | { type: 'REMOVE_TAX_ENTRY'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

  type TaxManagerContextType = {
    state: TaxState;
    loadTaxEntries: () => Promise<void>;
    addTaxEntry: (taxEntry: Omit<TaxEntry, 'id' | 'createdAt' | 'updatedAt' | 'budgetId'>) => Promise<void>;
    updateTaxEntry: (taxEntry: TaxEntry) => Promise<void>;
    removeTaxEntry: (taxEntryId: string) => Promise<void>;
    clearError: () => void;
  };

const TaxManagerContext = createContext<TaxManagerContextType | undefined>(undefined);

const initialState: TaxState = {
  taxEntries: [],
  loading: false,
  error: null,
};

function taxReducer(state: TaxState, action: TaxAction): TaxState {
  switch (action.type) {
    case 'LOAD_TAX_ENTRIES':
      return { ...state, taxEntries: action.payload, loading: false, error: null };
    case 'ADD_TAX_ENTRY':
      return { ...state, taxEntries: [...state.taxEntries, action.payload], loading: false, error: null };
    case 'UPDATE_TAX_ENTRY':
      return {
        ...state,
        taxEntries: state.taxEntries.map((entry) =>
          entry.id === action.payload.id ? action.payload : entry
        ),
        loading: false,
        error: null,
      };
    case 'REMOVE_TAX_ENTRY':
      return {
        ...state,
        taxEntries: state.taxEntries.filter((entry) => entry.id !== action.payload),
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

const taxEntryConverter: FirestoreDataConverter<TaxEntry> = {
  toFirestore: (taxEntry: TaxEntry) => {
    // Create a base object with all required fields
    const firestoreData: any = {
      taxType: taxEntry.taxType,
      name: taxEntry.name,
      amount: taxEntry.amount,
      frequency: taxEntry.frequency,
      dueDate: Timestamp.fromDate(taxEntry.dueDate),
      paid: taxEntry.paid,
      createdAt: Timestamp.fromDate(taxEntry.createdAt),
      updatedAt: Timestamp.fromDate(taxEntry.updatedAt),
      createdBy: taxEntry.createdBy,
      budgetId: taxEntry.budgetId,
      accountId: taxEntry.accountId
    };

    // Handle optional fields - explicitly set to null if undefined
    firestoreData.description = taxEntry.description ?? null;
    firestoreData.rate = taxEntry.rate ?? null;
    
    // Handle datePaid field
    if (taxEntry.paid && taxEntry.datePaid instanceof Date) {
      firestoreData.datePaid = Timestamp.fromDate(taxEntry.datePaid);
    } else {
      firestoreData.datePaid = null;
    }

    // Remove the id field as it's stored separately
    delete firestoreData.id;
    
    return firestoreData;
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): TaxEntry => {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
      dueDate: data.dueDate.toDate(),
      datePaid: data.datePaid ? data.datePaid.toDate() : undefined,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
      description: data.description ?? undefined,
      rate: data.rate ?? undefined
    } as TaxEntry;
  },
};

export function TaxManagerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(taxReducer, initialState);
  const { state: budgetState } = useBudgetManager();
  const { updateAccount } = useAccount();

  const checkUserAccess = async (userId: string, currentBudget: any) => {
    if (!currentBudget.isShared) {
      return true;
    }
  
    const budgetDocRef = doc(db, 'shared_budgets', currentBudget.id);
    const budgetDocSnap = await getDoc(budgetDocRef);
  
    if (!budgetDocSnap.exists()) {
      throw new Error('Shared budget not found');
    }
  
    const data = budgetDocSnap.data();
    const sharedWith = data?.sharedWith || [];
    return sharedWith.includes(userId) || data?.ownerId === userId;
  };

  const updateAccountBalance = async (accountId: string, amount: number, operation: 'add' | 'subtract') => {
    if (!accountId) return;
    
    const accountRef = doc(db, 'accounts', accountId);
    await runTransaction(db, async (transaction) => {
      const accountDoc = await transaction.get(accountRef);
      if (!accountDoc.exists()) {
        throw new Error('Account not found');
      }
      
      const accountData = accountDoc.data();
      const newBalance = operation === 'add' 
        ? accountData.balance + amount 
        : accountData.balance - amount;
      
      transaction.update(accountRef, { balance: newBalance });
    });
  };

  const loadTaxEntries = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget || !currentBudget.id) {
        console.warn('No budget selected or budget has no ID');
        dispatch({ type: 'LOAD_TAX_ENTRIES', payload: [] });
        return;
      }

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      const taxEntriesQuery = query(
        collection(db, 'tax_entries'),
        where('budgetId', '==', currentBudget.id),
        where('createdBy', '==', user.uid)
      ).withConverter(taxEntryConverter);

      const querySnapshot = await getDocs(taxEntriesQuery);
      const taxEntries = querySnapshot.docs.map((doc) => doc.data());

      dispatch({ type: 'LOAD_TAX_ENTRIES', payload: taxEntries });
    } catch (error) {
      console.error('Error loading tax entries:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load tax entries' });
    }
  }, [budgetState.currentBudget]);

  const addTaxEntry = useCallback(async (taxEntry: Omit<TaxEntry, 'id' | 'createdAt' | 'updatedAt' | 'budgetId'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');
  
      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }
  
      // Create the new tax entry with all required fields
      const newTaxEntry: TaxEntry = {
        ...taxEntry,
        createdBy: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        budgetId: currentBudget.id,
        paid: taxEntry.paid || false,
        datePaid: taxEntry.paid ? new Date() : undefined,
        description: taxEntry.description || undefined,
        rate: taxEntry.rate || undefined
      };

      // If paid, update account balance
      if (newTaxEntry.paid && newTaxEntry.accountId) {
        await updateAccountBalance(newTaxEntry.accountId, newTaxEntry.amount, 'subtract');
      }
  
      const taxEntriesCollection = collection(db, 'tax_entries').withConverter(taxEntryConverter);
      const docRef = await addDoc(taxEntriesCollection, newTaxEntry);
      
      const addedTaxEntry: TaxEntry = {
        ...newTaxEntry,
        id: docRef.id,
      };
  
      dispatch({ type: 'ADD_TAX_ENTRY', payload: addedTaxEntry });
    } catch (error) {
      console.error('Error adding tax entry:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add tax entry' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [budgetState.currentBudget]);

  
const updateTaxEntry = useCallback(async (taxEntry: TaxEntry) => {
  dispatch({ type: 'SET_LOADING', payload: true });
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    const currentBudget = budgetState.currentBudget;
    if (!currentBudget) throw new Error('No budget selected');

    const hasAccess = await checkUserAccess(user.uid, currentBudget);
    if (!hasAccess) {
      throw new Error('User does not have access to this budget');
    }

    if (!taxEntry.id) throw new Error('Tax entry ID is required for update');

    // Get the original tax entry
    const taxEntryRef = doc(db, 'tax_entries', taxEntry.id);
    const originalDoc = await getDoc(taxEntryRef);
    const originalTaxEntry = originalDoc.data();

    // Handle account balance updates
    if (taxEntry.accountId) {
      // If payment status changed
      if (originalTaxEntry?.paid !== taxEntry.paid) {
        if (taxEntry.paid) {
          await updateAccountBalance(taxEntry.accountId, taxEntry.amount, 'subtract');
        } else {
          await updateAccountBalance(taxEntry.accountId, taxEntry.amount, 'add');
        }
      } 
      // If amount changed while still paid
      else if (taxEntry.paid && originalTaxEntry?.amount !== taxEntry.amount) {
        const difference = taxEntry.amount - originalTaxEntry.amount;
        await updateAccountBalance(taxEntry.accountId, Math.abs(difference), difference > 0 ? 'subtract' : 'add');
      }
    }

    // Prepare update data with proper null handling
    const updateData = {
      ...taxEntry,
      updatedAt: new Date(),
      description: taxEntry.description ?? null,
      rate: taxEntry.rate ?? null,
      datePaid: taxEntry.paid ? (taxEntry.datePaid || new Date()) : null
    };

    // Remove the id from the update data
    delete updateData.id;

    await updateDoc(taxEntryRef.withConverter(taxEntryConverter), updateData);
    dispatch({ type: 'UPDATE_TAX_ENTRY', payload: taxEntry });
  } catch (error) {
    console.error('Error updating tax entry:', error);
    dispatch({ type: 'SET_ERROR', payload: 'Failed to update tax entry' });
  }
}, [budgetState.currentBudget]);

  const removeTaxEntry = useCallback(async (taxEntryId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      // Get the tax entry before deleting
      const taxEntryRef = doc(db, 'tax_entries', taxEntryId);
      const taxEntrySnap = await getDoc(taxEntryRef);
      const taxEntryData = taxEntrySnap.data();

      // If the tax was paid, add the amount back to the account
      if (taxEntryData?.paid && taxEntryData?.accountId) {
        await updateAccountBalance(taxEntryData.accountId, taxEntryData.amount, 'add');
      }

      await deleteDoc(taxEntryRef);
      dispatch({ type: 'REMOVE_TAX_ENTRY', payload: taxEntryId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to remove tax entry' });
    }
  }, [budgetState.currentBudget]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadTaxEntries();
      } else {
        dispatch({ type: 'LOAD_TAX_ENTRIES', payload: [] });
      }
    });

    return () => unsubscribe();
  }, [loadTaxEntries]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return (
    <TaxManagerContext.Provider
      value={{
        state,
        loadTaxEntries,
        addTaxEntry,
        updateTaxEntry,
        removeTaxEntry,
        clearError,
      }}
    >
      {children}
    </TaxManagerContext.Provider>
  );
}

export function useTaxManager(): TaxManagerContextType {
  const context = useContext(TaxManagerContext);
  if (!context) {
    throw new Error('useTaxManager must be used within a TaxManagerProvider');
  }
  return context;
}